import { z } from 'zod';
import { uuidSchema } from './common.schema';

export const CreateMilkPoolSchema = z.object({
  hospital_id: uuidSchema,
  provider_id: uuidSchema,
  created_by: uuidSchema,
  source_type: z.enum([ 'MOM', 'DONOR']),
  milk_type: z.string().max(20).optional(),
  current_location: z.string().max(200).optional(),
  total_volume_ml: z.number().positive().optional(),
  location: z.string().max(200).optional(),
  pasteurizer_equipment: z.string().max(200).optional(),
  receivable_ids: z.array(uuidSchema).min(1),
});

export type CreateMilkPoolRequest = z.infer<typeof CreateMilkPoolSchema>;

export const UpdateMilkPoolSchema = z.object({
  milk_type: z.string().max(20).optional(),
  pool_number: z.string().max(200).optional(),
  total_volume_ml: z.number().positive().optional(),
  location: z.string().max(200).optional(),
  expiry_at: z.string().datetime().optional(),
  pasteurizer_equipment: z.string().max(200).optional(),
  add_receivable_ids: z.array(uuidSchema).optional(),
  remove_receivable_ids: z.array(uuidSchema).optional(),
});

export type UpdateMilkPoolRequest = z.infer<typeof UpdateMilkPoolSchema>;

export const UpdatePasteurizationSchema = z.object({
  total_volume_ml: z.number().positive().optional(),
  source_type: z.enum(['MOTHER', 'MOM', 'DONOR']).optional(),
  pasteurizer_equipment: z.string().max(200).optional(),
  start_time: z.string().datetime().optional(),
  hold_time: z.number().int().positive().optional(),
  current_location: z.string().max(200).optional(),
  location: z.string().max(200).optional(),
  temperature: z.number().min(0).max(200).optional(),
});

export type UpdatePasteurizationRequest = z.infer<typeof UpdatePasteurizationSchema>;

export const MilkPoolResponseSchema = z.object({
  pool_id: uuidSchema,
  hospital_id: uuidSchema,
  pool_number: z.string().nullable(),
  provider_id: uuidSchema,
  provider_name: z.string().optional(),
  created_by: uuidSchema,
  source_type: z.string(),
  milk_type: z.string().nullable(),
  pool_status: z.string(),
  batch_creation_status: z.string(),
  total_volume_ml: z.number().nullable(),
  location: z.string().nullable(),
  expiry_at: z.string().nullable(),
  pasteurizer_equipment: z.string().nullable(),
  start_time: z.string().nullable(),
  hold_time: z.number().nullable(),
  current_location: z.string().nullable(),
  temperature: z.number().nullable(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  items: z.array(z.object({
    pool_item_id: uuidSchema,
    receivable_id: uuidSchema,
    receipt_number: z.string().optional(),
    provider_name: z.string().optional(),
    volume: z.number().nullable(),
    expiry_at: z.string().nullable(),
    storage_location_id: uuidSchema.nullable(),
    storage_location_name: z.string().nullable(),
    milk_status: z.string().nullable(),
    pump_date: z.string().nullable(),
  })),
});

export type MilkPoolResponse = z.infer<typeof MilkPoolResponseSchema>;

export const MilkPoolListQuerySchema = z.object({
  page:        z.coerce.number().int().positive().default(1),
  limit:       z.coerce.number().int().positive().max(100).default(25),
  status:      z.string().optional(),
  pool_number: z.string().optional(),
  hospital_id: uuidSchema.optional(),
});

export type MilkPoolListQuery = z.infer<typeof MilkPoolListQuerySchema>;

export const MilkPoolListItemSchema = z.object({
  pool_id:               uuidSchema,
  hospital_id:           uuidSchema,
  pool_number:           z.string().nullable(),
  location:              z.string().nullable(),
  total_volume_ml:       z.number().nullable(),
  expiry_at:             z.string().nullable(),
  created_at:            z.string(),
  created_by:            uuidSchema,
  pasteurizer_equipment: z.string().nullable(),
  source_type:           z.string(),
  milk_type:             z.string().nullable(),
  pool_status:           z.string(),
});

export type MilkPoolListItem = z.infer<typeof MilkPoolListItemSchema>;
